import styled, { css } from 'styled-components';
import { blockQuotes, longQuotes, pullQuotes } from 'styles/quotes';
import { headers } from 'styles/headers';
import { images } from 'styles/images';
import { breakpoint } from 'styles/globals';
import { paragraphs } from 'styles/paragraphs';
import { tables } from 'styles/tables';
import { videos } from 'styles/videos';
import SocialLinks from 'components/SocialLinks';
import { lists } from 'styles/lists';
import { accoladeWinnerStyles } from 'styles/widgets/accoladeWinner';
import { getBodyStyle, getColor } from 'styles/theme';
import type { Theme } from 'styles/theme';
import articleContentAnchorStyle from 'styles/articleContentAnchorStyles';
import { span } from 'utils/stringUtils';
import { articleContentStyle } from 'styles/articleContent';

const gridTemplate = {
  mobile: css<{ $hasTOC: boolean }>`
    grid-template-columns: repeat(4, 1fr);
    grid-template-areas: 
      "${span('topSection', 4)}"
      ${({ $hasTOC }) => ($hasTOC ? `"${span('toc', 4)}"` : '')}
      "${span('articleContent', 4)}"
      "${span('bottomSection', 4)}";
  `,
  tablet: css<{ $hasTOC: boolean }>`
    grid-template-columns: repeat(8, 1fr);
    grid-template-areas: 
      "${span('topSection', 8)}"
      ${({ $hasTOC }) => ($hasTOC ? `"${span('toc', 8)}"` : '')}
      "${span('articleContent', 8)}"
      "${span('bottomSection', 8)}"; 
  `,
  desktop: css<{ $hasTOC: boolean }>`
    max-width: 100%;
    grid-template-columns: ${({ $hasTOC }) =>
      $hasTOC ? 'minmax(237px, 361px) minmax(650px, 700px) 1fr' : '1fr minmax(0, 700px) 1fr'};
    grid-template-areas:
      "${span('topSection', 3)}"
      "${({ $hasTOC }) => ($hasTOC ? `toc articleContent .` : '. articleContent .')}"
      "${({ $hasTOC }) => ($hasTOC ? `toc bottomSection .` : '. bottomSection .')}";
  `,
};

export const LandingPageWrapper = styled.div<{ $headerHeight: number; $hasTOC: boolean }>`
  display: grid;
  grid-template-rows: auto;
  gap: 40px var(--Grid-Gap);

  & > .top-section {
    grid-area: topSection;
  }

  & > .toc {
    grid-area: toc;
    position: sticky;
    z-index: 1000;
    top: ${({ $headerHeight }) => `${$headerHeight}px`};
  }

  & > .article-content {
    grid-area: articleContent;
  }

  & > .bottom-section {
    grid-area: bottomSection;
  }

  ${gridTemplate.mobile};

  @media (${breakpoint.mdMin}) {
    ${gridTemplate.tablet};
  }

  @media (${breakpoint.lgMin}) {
    ${gridTemplate.desktop};
  }
`;

const stickyTOCHeight = {
  mobile: 56,
  tablet: 80,
};

const scrollMarginTop = css<{ $headerHeight: number }>`
  scroll-margin-top: ${({ $headerHeight }) => `${$headerHeight + stickyTOCHeight.mobile + 24}px`};

  @media (${breakpoint.mmMin}) {
    scroll-margin-top: ${({ $headerHeight }) => `${$headerHeight + stickyTOCHeight.tablet + 24}px`};
  }
  @media (${breakpoint.lgMin}) {
    scroll-margin-top: ${({ $headerHeight }) => `${$headerHeight + 24}px`};
  }
`;

export const ArticleContent = styled.div<{ theme: Theme; $headerHeight: number; $hasTOC?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 40px;
  max-width: min(100%, 700px);
  ${getBodyStyle('georgia', { default: 'medium' })};
  margin: auto;

  ${paragraphs};
  ${headers};
  ${images};
  ${blockQuotes};
  ${pullQuotes};
  ${longQuotes};
  ${videos};
  ${tables};
  ${lists};
  ${articleContentAnchorStyle};
  ${articleContentStyle};
  ${accoladeWinnerStyles};

  div[class^="rawHtml-content"] {
    h1, h2, h3, h4, h5, h6 {
      ${scrollMarginTop}
    }
  }

  @media (${breakpoint.lgMin}) {
    // if there's no TOC on the left keep the article content centered
    ${({ $hasTOC }) => (!$hasTOC ? css`margin: auto;` : css`margin: unset;`)}
  }
`;

export const TopSection = styled.div<{ theme: Theme; $hasTOC: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 40px;

  //removes the grid gap before the TOC area on mobile
  ${({ $hasTOC }) => ($hasTOC ? css`margin-bottom: -40px;` : '')}

  & > .landing-page-header,
  & > .accolades-header {
    margin: 0 calc((-1 * var(--Grid-Margin)));
  }

  @media (${breakpoint.lgMin}) {
    margin-bottom: 0;
  }

  @media (${breakpoint.xxlMin}) {
    // the accolades and landing page headers should overflow the page content
    // and take the entire width of the window
    & > .landing-page-header,
    & > .accolades-header {
      margin: 0 calc((1440px - 100vw - (2 * var(--Grid-Margin))) / 2);
    }
  }
`;

export const BottomSection = styled.div<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  gap: 40px;

  .disclosure {
    ${paragraphs};
    ${getBodyStyle('georgia', { default: 'medium' })};
  }
`;

export const LandingPageSocialContainer = styled.div`
  text-align: left;

  svg {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (${breakpoint.mdMin}) {
    margin-bottom: 0px;
  }

  @media (${breakpoint.lgMin}) {

    a.social::before {
      width: 32px;
      height: 32px;
      mask-size: 32px 32px;
    }
  }
`;

export const LandingPageSocialShare = styled(SocialLinks)<{ theme: Theme }>`
  border: 1px solid ${getColor('borderSecondary')};
  border-radius: 4px;
  padding: 8px;
  display: inline-flex;
  flex-direction: row;

  @media (${breakpoint.mdMin}) {
    position: static;
    border-bottom: 1px solid ${getColor('borderSecondary')};
    padding: 8px;
    li {
      margin-right: 16px;
      margin-bottom: 0;
    }
  }
`;

export const AuthorsBioCards = styled.div<{ theme: Theme }>`
  border-top: 3px solid ${getColor('borderAccentPrimary')};
 
  h4 {
    margin-top: 8px;
    margin-bottom: 16px;
  }

  .author-bio {
    ul {
      list-style-type: none;
      padding: 0;
    }
  }
`;
